import { useCallback, useEffect, useRef, useState } from "react";
import type { KeyboardEvent as ReactKeyboardEvent } from "react";
import { HiChevronDown } from "react-icons/hi";
import { useLocation } from "react-router-dom";

import { newNavExcludesNavigation } from "../../Utilities/Navigation/utils";
import { useUser } from "../../Hooks/useUser";
import { useIsDesktopApp } from "../../Hooks/useIsDesktopApp";
import { useIsMobileApp } from "../../Hooks/useIsMobileApp";
import { NAV_BAR, NAV_CATEGORIES, findActiveCategoryKey } from "./links";
import { MegaMenu } from "./MegaMenu";
import { MobileNav } from "./MobileNav";
import { SuperNav } from "./SuperNav";
import {
  CategoryLink,
  CategoryRow,
  CategoryTrigger,
  HomeLink,
  NavBar,
  NavInner,
  StyledHiHome,
} from "./styledComponents";

const HOVER_INTENT_MS = 150;
/**
 * Switching between open panels waits too, rather than swapping on contact.
 * Travelling from a trigger down into the panel often clips a neighbouring
 * trigger on the way, and an instant swap meant the panel changed underneath
 * the cursor before it arrived. Leaving the trigger cancels the pending open,
 * so a pass-through costs nothing while a deliberate hover still lands.
 */
const SWITCH_INTENT_MS = 180;
// A click that lands just after hover-intent opened the panel means "open",
// not "close". Without this the menu shuts under anyone who points and clicks.
const CLICK_GRACE_MS = 400;
const CLOSE_INTENT_MS = 320;

const SiteNav = ({ standalone }: { standalone?: boolean }) => {
  const { pathname } = useLocation();
  const isDesktopView = useIsDesktopApp();
  const isMobileApp = useIsMobileApp();
  /**
   * useUser, not the IsUserLoggedIn atom: that atom computes its default once
   * at module load from localStorage and nothing ever sets it, so it does not
   * flip until a full page reload. Reading it here left the bar in its
   * signed-out shape until you refreshed.
   */
  const { allstarUser } = useUser();
  const isLoggedIn = Boolean(allstarUser?.loggedIn);

  const [openKey, setOpenKey] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const triggerRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const openTimer = useRef<ReturnType<typeof setTimeout>>();
  const closeTimer = useRef<ReturnType<typeof setTimeout>>();
  const openedAt = useRef(0);
  const openedByHover = useRef(false);

  const barObserver = useRef<ResizeObserver>();
  const barRef = useCallback((node: HTMLElement | null) => {
    barObserver.current?.disconnect();

    if (!node) {
      document.documentElement.style.removeProperty("--site-header-height");
      return;
    }

    barObserver.current = new ResizeObserver(() =>
      document.documentElement.style.setProperty(
        "--site-header-height",
        `${node.offsetHeight}px`,
      ),
    );
    barObserver.current.observe(node);
  }, []);

  // Standalone pages mount the nav themselves, and their own routes sit in
  // excludedPaths, so the path guard would wrongly blank them.
  const hidden =
    (!standalone && newNavExcludesNavigation(pathname)) ||
    isDesktopView.isDesktop ||
    isMobileApp.isMobileApp;

  // Warms the panel art on mount so the first panel does not open onto an
  // empty rail. Effects run after paint, so this stays off the critical path.
  useEffect(() => {
    if (hidden) return;
    NAV_CATEGORIES.forEach(({ art, glass }) =>
      [art, glass].forEach((src) => {
        if (src) new Image().src = src;
      }),
    );
  }, [hidden]);

  const clearTimers = useCallback(() => {
    if (openTimer.current) clearTimeout(openTimer.current);
    if (closeTimer.current) clearTimeout(closeTimer.current);
  }, []);

  useEffect(() => clearTimers, [clearTimers]);

  // Navigating with a panel open would otherwise leave it hanging over the new
  // page.
  useEffect(() => {
    setOpenKey(null);
    setMobileOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!openKey) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setOpenKey(null);
      triggerRefs.current[openKey]?.focus();
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [openKey]);

  const handleEnter = (key: string) => {
    clearTimers();
    openTimer.current = setTimeout(
      () => markOpened(key, true),
      openKey ? SWITCH_INTENT_MS : HOVER_INTENT_MS,
    );
  };

  const markOpened = (key: string, byHover: boolean) => {
    openedAt.current = Date.now();
    openedByHover.current = byHover;
    setOpenKey(key);
  };

  const handleTriggerClick = (key: string) => {
    clearTimers();

    if (openKey !== key) {
      markOpened(key, false);
      return;
    }

    // Opened by hover a moment ago, so treat this click as intent to open.
    if (
      openedByHover.current &&
      Date.now() - openedAt.current < CLICK_GRACE_MS
    ) {
      openedByHover.current = false;
      return;
    }

    setOpenKey(null);
  };

  const handleLeave = () => {
    clearTimers();
    closeTimer.current = setTimeout(() => setOpenKey(null), CLOSE_INTENT_MS);
  };

  // One bar and one category set for both auth states. The strip above is
  // permanent too, so signing in swaps its contents rather than moving them.
  const categories = NAV_CATEGORIES;
  const barEntries = NAV_BAR;
  const activeKey = findActiveCategoryKey(pathname, categories);
  const openCategory = categories.find((category) => category.key === openKey);
  const handleTriggerKeyDown = (
    event: ReactKeyboardEvent<HTMLButtonElement>,
    index: number,
  ) => {
    if (event.key !== "ArrowRight" && event.key !== "ArrowLeft") return;
    event.preventDefault();
    const step = event.key === "ArrowRight" ? 1 : -1;
    const next = (index + step + categories.length) % categories.length;
    triggerRefs.current[categories[next].key]?.focus();
  };

  if (hidden) return <></>;

  // Shared by both cohorts so the trigger's behaviour cannot drift between them.
  const renderTrigger = (
    category: (typeof categories)[number],
    index: number,
  ) => (
    <CategoryTrigger
      key={category.key}
      ref={(node) => {
        triggerRefs.current[category.key] = node;
      }}
      $open={openKey === category.key}
      aria-current={activeKey === category.key ? "page" : undefined}
      aria-expanded={openKey === category.key}
      aria-controls={`nav-panel-${category.key}`}
      onClick={() => handleTriggerClick(category.key)}
      onKeyDown={(event) => handleTriggerKeyDown(event, index)}
      onMouseEnter={() => handleEnter(category.key)}
      onMouseLeave={handleLeave}
    >
      {category.label}
      <HiChevronDown aria-hidden="true" />
    </CategoryTrigger>
  );

  return (
    <NavBar ref={barRef} $standalone={standalone}>
      <SuperNav
        isLoggedIn={isLoggedIn}
        pathname={pathname}
        standalone={standalone}
        onOpenMobile={() => setMobileOpen(true)}
      />
      <NavInner $standalone={standalone}>
        <CategoryRow aria-label="Primary">
          <HomeLink to="/" aria-label="Home">
            <StyledHiHome aria-hidden="true" />
          </HomeLink>
          {barEntries.map((entry, index) => {
            if (entry.kind === "link")
              return (
                <CategoryLink key={entry.to} to={entry.to}>
                  {entry.label}
                </CategoryLink>
              );

            const category = categories.find((c) => c.key === entry.key);
            return category ? renderTrigger(category, index) : null;
          })}
        </CategoryRow>
      </NavInner>
      {openCategory && (
        <MegaMenu
          category={openCategory}
          isLoggedIn={isLoggedIn}
          panelId={`nav-panel-${openCategory.key}`}
          pathname={pathname}
          standalone={standalone}
          onNavigate={() => setOpenKey(null)}
          onMouseEnter={clearTimers}
          onMouseLeave={handleLeave}
        />
      )}
      <MobileNav
        open={mobileOpen}
        isLoggedIn={isLoggedIn}
        onClose={() => setMobileOpen(false)}
      />
    </NavBar>
  );
};

export { SiteNav };
